import * as React from "react";
import { useContext, useState } from "react";
import { Genome } from "../../../api";
import { StoresContext } from "../../../pages/_app";
import { datetimeFormatter } from "../../../utils/dates";
import {
  MetadataCard,
  MetadataCardsWrapper,
  MetadataRow,
  MetadataVerticalCards,
} from "../../metadata-card";
import { MultipleValues } from "../../multiple-values";
import { SelectLotNumber } from "../../select-lot-number";
import { DownloadSelectedButton } from "../genomes/download-selected-button";

export const CellBioDetailsOverviewPanel = ({ genome }: { genome: Genome }) => {
  const [wesSeqLotNumberIndex, setWesSeqLotNumberIndex] = useState<number>(0);
  const [rnaSeqLotNumberIndex, setRnaSeqLotNumberIndex] = useState<number>(0);
  const rootStore = useContext(StoresContext);

  if (!genome.attributes.atcc_metadata) {
    return null;
  }
  const meta = genome.attributes.atcc_metadata;
  const { wes_sequencing, rna_sequencing } = meta;

  const rna_sequencing_lot_numbers = Object.keys(rna_sequencing?.lot_number ?? {});
  const wes_sequencing_lot_numbers = Object.keys(wes_sequencing?.lot_number ?? {});

  const rna_sequencing_stats = rna_sequencing
    ? Object.values(rna_sequencing.lot_number)[rnaSeqLotNumberIndex]!.qc_statistics
    : null;

  const wes_sequencing_stats = wes_sequencing
    ? Object.values(wes_sequencing.lot_number)[wesSeqLotNumberIndex]!.qc_statistics
    : null;

  return (
    <div className="metadata-page">
      <div className="download-buttons">
        <DownloadSelectedButton
          downloadInfos={genome.datasets}
          initBulkSearch={(downloadTypes) => {
            return rootStore.cellBioStore.initSingleGenomeSearch(genome.uuid, downloadTypes);
          }}
          initBulkDownload={(downloadTypes) => {
            return rootStore.cellBioStore.initSingleGenomeDownload(genome.uuid, downloadTypes);
          }}
          hasRequiredSubscription={rootStore.userStore.hasActiveCellBioSubscription}
          large
          showLotIdOption
        />
      </div>

      <MetadataCardsWrapper>
        <MetadataCard title="Organism Summary">
          {meta.cell_line_name ? (
            <MetadataRow label="Item Name" value={meta.cell_line_name} />
          ) : null}
          {meta.morphology ? <MetadataRow label="Morphology" value={meta.morphology} /> : null}
          {meta.product_application ? (
            <MetadataRow
              label="Product Application"
              value={<MultipleValues values={meta.product_application.split("|")} />}
            />
          ) : null}
          {meta.bsl ? <MetadataRow label="Biosafety Level" value={meta.bsl} /> : null}
          {meta.gender ? <MetadataRow label="Gender" value={meta.gender} /> : null}
          {meta.tissue ? (
            <MetadataRow
              label="Tissue"
              value={<MultipleValues values={meta.tissue.split("|")} />}
            />
          ) : null}
          {meta.cell_type ? <MetadataRow label="Cell Type" value={meta.cell_type} /> : null}
          {meta.age ? <MetadataRow label="Age" value={meta.age} /> : null}

          {meta.product_type ? (
            <MetadataRow
              label="Product Type"
              value={<MultipleValues values={meta.product_type.split("|")} />}
            />
          ) : null}

          {meta.disease ? (
            <MetadataRow
              label="Disease"
              value={<MultipleValues values={meta.disease.split("|")} />}
            />
          ) : null}

          {meta.strain_name ? <MetadataRow label="Strain" value={meta.strain_name} /> : null}
        </MetadataCard>

        {wes_sequencing_stats || rna_sequencing_stats ? (
          <MetadataVerticalCards>
            {wes_sequencing_stats ? (
              <MetadataCard
                title="Whole Exome Sequencing Summary"
                right={
                  wes_sequencing_lot_numbers.length > 1 ? (
                    <SelectLotNumber
                      lotNumbers={wes_sequencing_lot_numbers}
                      value={wesSeqLotNumberIndex}
                      onChange={setWesSeqLotNumberIndex}
                    />
                  ) : null
                }
              >
                {wes_sequencing_stats.sample_summary.pipeline ? (
                  <MetadataRow
                    label="Bioinformatics Pipeline"
                    value={wes_sequencing_stats.sample_summary.pipeline}
                  />
                ) : null}
                <MetadataRow label="Date Published" value={datetimeFormatter(genome.created_at)} />
                {wes_sequencing_stats.illumina_metadata.sequencer ? (
                  <MetadataRow
                    label="Sequencing Technology"
                    value={wes_sequencing_stats.illumina_metadata.sequencer}
                  />
                ) : null}
                {wes_sequencing_stats.sample_summary.replicate_count ? (
                  <MetadataRow
                    label="Number of Replicates"
                    value={wes_sequencing_stats.sample_summary.replicate_count}
                  />
                ) : null}
                {(wes_sequencing_stats.analysis_results.number_of_variants_passed_filter?.length ??
                  0) > 0 ? (
                  <MetadataRow
                    label="Number of variants (> 95% freq, > 30X depth)"
                    value={
                      <MultipleValues
                        values={(
                          wes_sequencing_stats.analysis_results.number_of_variants_passed_filter ??
                          []
                        ).map((x) => `${x}`)}
                      />
                    }
                  />
                ) : null}
                {(wes_sequencing_stats.analysis_results.number_of_clinvar_variants_passed_filter
                  ?.length ?? 0) > 0 ? (
                  <MetadataRow
                    label="Number of ClinVar Variants"
                    value={
                      <MultipleValues
                        values={(
                          wes_sequencing_stats.analysis_results
                            .number_of_clinvar_variants_passed_filter ?? []
                        ).map((x) => `${x}`)}
                      />
                    }
                  />
                ) : null}
                {(wes_sequencing_stats.analysis_results.msi_score?.length ?? 0) > 0 ? (
                  <MetadataRow
                    label="MSI Score"
                    value={
                      <MultipleValues
                        values={wes_sequencing_stats.analysis_results.msi_score ?? []}
                      />
                    }
                  />
                ) : null}
              </MetadataCard>
            ) : null}
            {rna_sequencing_stats ? (
              <MetadataCard
                title="RNASeq Summary"
                right={
                  rna_sequencing_lot_numbers.length > 1 ? (
                    <SelectLotNumber
                      lotNumbers={rna_sequencing_lot_numbers}
                      value={rnaSeqLotNumberIndex}
                      onChange={setRnaSeqLotNumberIndex}
                    />
                  ) : null
                }
              >
                {rna_sequencing_stats.sample_summary.pipeline ? (
                  <MetadataRow
                    label="Bioinformatics Pipeline"
                    value={rna_sequencing_stats.sample_summary.pipeline}
                  />
                ) : null}
                <MetadataRow label="Date Published" value={datetimeFormatter(genome.created_at)} />
                {rna_sequencing_stats.illumina_metadata.sequencer ? (
                  <MetadataRow
                    label="Sequencing Technology"
                    value={rna_sequencing_stats.illumina_metadata.sequencer}
                  />
                ) : null}
                {rna_sequencing_stats.sample_summary.replicate_count ? (
                  <MetadataRow
                    label="Number of Replicates"
                    value={rna_sequencing_stats.sample_summary.replicate_count}
                  />
                ) : null}
              </MetadataCard>
            ) : null}
          </MetadataVerticalCards>
        ) : null}
      </MetadataCardsWrapper>
    </div>
  );
};
